import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, OutlinedInput, Typography, useMediaQuery } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import LoadingApp from "../../../components/LoadingApp";
import ModalFeedBack from "../../../components/ModalFeedBack";
import { Doctor } from "../../../interfaces/entities/Doctor";
import { PrestadorConsultado } from "../../../interfaces/entities/Permissoes/PrestadorConsultado";
import { adicionarPrestadorConfianca } from "../../../services/PermissoesService";
import { getMedicos } from "../../../services/SaudeOnlineService";
import AddAllowedDoctorCard from "./AddAllowedDoctorCard";
import AllowedDoctorCard from "./AllowedDoctorCard";
import "./styles.css";
import { sxButton, sxFindDoctorBox, sxInput, sxLabel, sxLabeledOutlinedInput } from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import DefaultPagination from "../../../components/Accessibility/DefaultPagination";

const AllowedDoctorsForm: React.FC<{
  doctorsType: 'consulted' | 'trusted',
  doctors?: PrestadorConsultado[],
  toggleDoctorPermission: (doctor: PrestadorConsultado) => void,
  fetchDoctors?: () => void
}> = ({ doctors, toggleDoctorPermission, doctorsType, fetchDoctors }) => {

  const { textStyleAccessibility, contrastBorderAccessibility, contrastStyle, buttonTextStyleAccessibility, buttonStyleAccessibility } = useAccessibility();

  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const [loading, setLoading] = useState<boolean>(false);
  const emptyMessage = (
    doctorsType === 'consulted' ? 'Não foram encontrados médicos consultados.' : 'Você ainda não adicionou nenhum médico de confiança.'
  );
  const [searchedDoctors, setSearchedDoctors] = useState<boolean>(false);
  const [doctorsSearchResult, setDoctorsSearchResult] = useState<Doctor[]>([]);
  const [addingTrustedDoctor, setAddingTrustedDoctor] = useState<boolean>(false);
  const [nomeMedico, setNomeMedico] = useState<string>('');
  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);
  const [page, setPage] = useState<number>(1);
  const [paginatedDoctorsSearchResult, setPaginatedDoctorsSearchResult] = useState<Doctor[]>([]);
  const pageSize = 10;

  const doctorsListRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!addingTrustedDoctor) {
      doctorsListRef?.current?.focus();
    }
  }, [addingTrustedDoctor]);

  const toggleAddingTrustedDoctor = () => {
    setAddingTrustedDoctor(adding => !adding);
  }

  const clearSearchedDoctors = () => {
    setDoctorsSearchResult([]);
    toggleAddingTrustedDoctor();
    setSearchedDoctors(false);
    setNomeMedico('');
  }

  const addTrustedDoctor = async (idPessoa: number) => {
    const response = await adicionarPrestadorConfianca(idPessoa);
    if (response?.status === 200) {
      openModalSuccess('Médico(a) adicionado com sucesso!');
    } else {
      const errorMessage = !!response?.data.mensagem ? response.data.mensagem : 'Tente novamente em instantes';
      openModalError('Erro ao adicionar médico de confiança', errorMessage);
    }
  }

  const findDoctorsByName = async () => {
    setLoading(true);
    setSearchedDoctors(true);
    const response = await getMedicos(nomeMedico);
    if (response?.status === 200) {
      const doctorsSearchData: Doctor[] = response.data;
      setDoctorsSearchResult(doctorsSearchData);
    } else {
      console.error(response);
    }
    setLoading(false);
  }

  const closeAddDoctorFeedback = () => {
    setModalFeedback(null);
    clearSearchedDoctors();
    if (fetchDoctors) {
      fetchDoctors();
    }
  }

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: closeAddDoctorFeedback,
      buttonAction: closeAddDoctorFeedback
    });
  }

  const openModalSuccess = (title: string) => {
    setModalFeedback({
      icon: 'success',
      title: title,
      showButtonModal: false,
      closeAction: closeAddDoctorFeedback,
      buttonAction: closeAddDoctorFeedback
    });
  }

  const handlePressEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      findDoctorsByName();
    }
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    const start = (page - 1) * pageSize;
    const paginatedData = doctorsSearchResult.slice(start, start + pageSize);
    setPaginatedDoctorsSearchResult(paginatedData);
  };

  useEffect(() => {
    handlePageChange(1);
  }, [doctorsSearchResult]);

   return (
    <>
      <LoadingApp showLoading={loading} text={'Carregando...'} />
      <ul className='allowed-doctors-holder' ref={doctorsListRef} tabIndex={-1}>
        {!addingTrustedDoctor ?
          <>
            {!doctors?.length ?
              <div className="advice-container" style={{ ...contrastStyle, ...contrastBorderAccessibility }} role="status" aria-live="polite">
                <Typography style={textStyleAccessibility}>{emptyMessage}</Typography>
              </div>
              :
              <>
                {doctors?.map((doctor) => (
                  <AllowedDoctorCard
                    key={doctor.idPrestador}
                    doctorConsulted={doctor}
                    handleChangeDoctorPermission={() => toggleDoctorPermission(doctor)}
                    fetchDoctors={fetchDoctors}
                    showExcluir={doctorsType === 'trusted'}
                  />
                ))}
              </>
            }
          </>
          :
          <Box sx={sxFindDoctorBox}>
            <div className="find-doctor-container">
              <Box sx={sxLabeledOutlinedInput}>
                <Typography style={textStyleAccessibility} sx={sxLabel}>Pesquisar nome do(a) médico(a)</Typography>
                <label aria-label="Pesquisar nome do médico" htmlFor="pesquisarNomeMedico" />
                <OutlinedInput
                  id="pesquisarNomeMedico"
                  type={'text'}
                  style={textStyleAccessibility}
                  value={nomeMedico}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^a-zA-ZçÇáéíóúÁÉÍÓÚãõÃÕ\s]/g, ''); //Digita APENAS LETRAS
                    setNomeMedico(value);
                  }}
                  onKeyDown={handlePressEnter}
                  name='nomeMedico'
                  inputProps={{ maxLength: 30 }}
                  maxRows={1}
                  sx={sxInput}
                />
              </Box>
              <Button
                variant="contained"
                size="large"
                onClick={findDoctorsByName}
                type="button"
                sx={sxButton}
                style={buttonStyleAccessibility}
                fullWidth={mobileScreen}
              >
                <Typography style={buttonTextStyleAccessibility}>PESQUISAR</Typography>
              </Button>
            </div>
            {searchedDoctors &&
              <ul className="allowed-doctors-holder">
                {!!doctorsSearchResult.length ?
                  <>
                    {paginatedDoctorsSearchResult.map((doctor) => (
                      <AddAllowedDoctorCard key={doctor.nomePessoa} doctor={doctor} handleAddTrustedDoctor={addTrustedDoctor} />
                    ))
                    }
                  </>
                  :
                  <Typography style={textStyleAccessibility}>
                    Nenhum registro localizado.
                  </Typography>
                }
              </ul>
            }
            <DefaultPagination 
              currentPage={page} 
              totalPages={Math.ceil(doctorsSearchResult.length / pageSize)} 
              handlePageChange={handlePageChange} 
            />
            <button
              className="back-button"
              onClick={clearSearchedDoctors}
              style={textStyleAccessibility}
            >
              <FontAwesomeIcon icon={faChevronLeft} />&nbsp;&nbsp;<u>Voltar</u>
            </button>
          </Box>
        }
        </ul>
        {(doctorsType === 'trusted' && !addingTrustedDoctor) &&
          <Button
            variant="contained"
            sx={sxButton}
            style={buttonStyleAccessibility}
            onClick={toggleAddingTrustedDoctor}
            fullWidth={mobileScreen}
          >
            <Typography style={buttonTextStyleAccessibility}>ADICIONAR MÉDICO(A)</Typography>
          </Button>
        }
      <ModalFeedBack
        showModal={!!modalFeedback}
        handleShowModal={modalFeedback?.closeAction}
        title={modalFeedback?.title as string}
        subTitle={modalFeedback?.subTitle as string}
        icon={modalFeedback?.icon as 'success' | 'error' | 'warning'}
        showButtonModal={modalFeedback?.showButtonModal}
        textButton={modalFeedback?.textButton as string}
        handleButtonModal={modalFeedback?.buttonAction}
      />
    </>
  )
}

export default AllowedDoctorsForm;